import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Cloud, Zap, Code, Shield, Star, ChevronRight, TrendingUp } from 'lucide-react';
import SEO from '../components/SEO';
import QivratechLogo from '../components/QivratechLogo';
import ProductsSection from '../components/ProductsSection';

const HomePage = () => {
  const [hoveredService, setHoveredService] = useState(null);

  const services = [
    {
      icon: <Brain className="w-12 h-12" />,
      title: "AI & Machine Learning Solutions",
      description: "Predictive analytics, intelligent automation, and custom AI applications that drive measurable business outcomes.",
      features: ["Neural Networks", "Deep Learning", "Natural Language Processing", "Computer Vision"],
      color: "from-cyan-400 to-blue-500"
    },
    {
      icon: <Cloud className="w-12 h-12" />,
      title: "Cloud & Digital Transformation",
      description: "Cloud migration, DevOps implementation, and hybrid-cloud architecture for scalable operations.",
      features: ["AWS/Azure/GCP", "Kubernetes", "Microservices", "DevOps"],
      color: "from-purple-400 to-pink-500"
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Strategic IT Consulting",
      description: "Technology roadmaps, system integration, and optimization strategies for intelligent decision-making.",
      features: ["Digital Strategy", "Enterprise Architecture", "Process Optimization", "Technology Audit"],
      color: "from-yellow-400 to-orange-500"
    },
    {
      icon: <Code className="w-12 h-12" />,
      title: "Custom Software & SaaS Development",
      description: "Web and mobile applications, APIs, and microservices architecture tailored to your needs.",
      features: ["React/Node.js", "Mobile Apps", "API Development", "SaaS Platforms"],
      color: "from-green-400 to-teal-500"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Cybersecurity & Managed IT",
      description: "Security audits, continuous monitoring, and comprehensive risk management solutions.",
      features: ["Penetration Testing", "Security Audits", "Compliance", "24/7 Monitoring"],
      color: "from-red-400 to-pink-500"
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Marketing Intelligence",
      description: "AI-driven digital marketing, performance analytics, and automated growth strategies that maximize ROI.",
      features: ["AI Marketing", "Performance Analytics", "SEO Automation", "CRM Integration"],
      color: "from-indigo-400 to-purple-500"
    }
  ];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Qivra Technologies - AI & IT Solutions | Digital Transformation Experts"
        description="Transform your business with Qivra Technologies' cutting-edge AI, machine learning, cloud computing, and IT consulting services. Intelligence unleashed for your success."
        keywords="AI solutions, machine learning, cloud computing, digital transformation, IT consulting, software development, cybersecurity, enterprise solutions"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        {/* Enhanced Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(14, 165, 233, 0.05) 0%, transparent 50%)
              `
            }}
          />

          {/* Floating Logo Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-20 left-10 opacity-10"
              animate={{
                y: [0, -30, 0],
                x: [0, 15, 0],
                rotate: [0, 5, 0]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute top-32 right-20 opacity-8"
              animate={{
                y: [0, -20, 0],
                x: [0, -10, 0],
                rotate: [0, -3, 0]
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-32 left-20 opacity-6"
              animate={{
                y: [0, -25, 0],
                x: [0, 20, 0],
                rotate: [0, 8, 0]
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 4
              }}
            >
              <QivratechLogo variant="mark" size="small" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-20 right-10 opacity-12"
              animate={{
                y: [0, -35, 0],
                x: [0, -15, 0],
                rotate: [0, -6, 0]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 6
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute top-40 left-1/3 opacity-5"
              animate={{
                y: [0, -15, 0],
                x: [0, 25, 0],
                rotate: [0, 4, 0]
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-40 right-1/3 opacity-7"
              animate={{
                y: [0, -40, 0],
                x: [0, -20, 0],
                rotate: [0, -5, 0]
              }}
              transition={{
                duration: 14,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3
              }}
            >
              <QivratechLogo variant="mark" size="small" theme="dark" />
            </motion.div>
          </div>
        </div>

        <motion.div
          className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-semibold">AI & IT Solutions</span>
            </div>

            <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-thin mb-6 leading-tight tracking-tight">
              <span className="text-white font-extralight">
                Qivratech
              </span>
            </h1>

            <div className="mb-12">
              <span className="text-2xl sm:text-3xl md:text-4xl font-light tracking-widest bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent">
                Intelligence Unleashed
              </span>
            </div>

            <p className="text-xl text-gray-300 mb-16 max-w-2xl mx-auto font-light leading-relaxed">
              Transform your Business with AI-powered solutions
            </p>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-6 justify-center"
          >
            <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/contact"
                className="bg-gradient-to-r from-cyan-400 to-purple-600 px-10 py-5 rounded-2xl font-light tracking-wide text-white hover:shadow-2xl hover:shadow-cyan-400/40 transition-all duration-300 flex items-center gap-3 border border-cyan-400/40 group"
              >
                <span>Talk to an Expert</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/projects"
                className="border-2 border-cyan-400/60 text-cyan-400 px-10 py-5 rounded-2xl font-light tracking-wide hover:bg-cyan-400/10 hover:border-cyan-400 transition-all duration-300 flex items-center gap-3"
              >
                <span>View Case Studies</span>
                <ArrowRight className="w-5 h-5 hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </motion.div>

        </motion.div>

        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
      </section>

      {/* Services Preview Section */}
      <section className="py-32 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Our Expertise</span>
            </div>

            <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 leading-tight tracking-wide">
              <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent">
                Solutions
              </span>
            </h2>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
              Comprehensive AI and IT services designed to accelerate your Digital transformation journey
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                viewport={{ once: true }}
                whileHover={{ y: -8, scale: 1.01 }}
                onHoverStart={() => setHoveredService(idx)}
                onHoverEnd={() => setHoveredService(null)}
                className="group relative bg-gradient-to-b from-gray-900/50 to-black/80 backdrop-blur-sm p-10 rounded-3xl border border-gray-800/60 hover:border-cyan-400/40 transition-all duration-500 overflow-hidden"
              >
                {/* Enhanced glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative z-10">
                  <div className="text-cyan-400 mb-8 group-hover:scale-105 group-hover:text-cyan-300 transition-all duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-light text-white mb-6 group-hover:text-cyan-300 transition-colors duration-300 tracking-wide leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 mb-8 leading-relaxed font-light tracking-wide">
                    {service.description}
                  </p>

                  <motion.div
                    initial={false}
                    animate={{ height: hoveredService === idx ? 'auto' : 0, opacity: hoveredService === idx ? 1 : 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="mb-6">
                      <div className="flex flex-wrap gap-3">
                        {service.features.map((feature, featureIdx) => (
                          <motion.span
                            key={featureIdx}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: hoveredService === idx ? 1 : 0, scale: hoveredService === idx ? 1 : 0.9 }}
                            transition={{ delay: featureIdx * 0.05 }}
                            className="bg-gradient-to-r from-cyan-500/20 to-purple-600/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-light border border-cyan-400/30"
                          >
                            {feature}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  </motion.div>

                  <Link
                    to="/solutions"
                    className="inline-flex items-center gap-3 text-cyan-400 font-light tracking-wide hover:text-cyan-300 transition-colors duration-300 group/link"
                  >
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-2 transition-transform duration-300" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link
              to="/solutions"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-400 to-purple-600 px-10 py-5 rounded-2xl font-light tracking-wide text-white hover:shadow-2xl hover:shadow-cyan-400/40 transition-all hover:scale-105 duration-300 border border-cyan-400/40 group"
            >
              <span>Explore All Solutions</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <ProductsSection />

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              radial-gradient(circle at 30% 70%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 70% 30%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
            `
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Ready to Start?</span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight mb-8 text-white leading-tight tracking-wide">
              Transform Your
              <span className="block bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent font-light mt-2">
                Business Today
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 font-light leading-relaxed tracking-wide max-w-3xl mx-auto">
              Let's discuss how our AI and IT solutions can accelerate your growth and innovation
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-cyan-400 to-purple-600 px-10 py-5 rounded-2xl font-light tracking-wide text-white hover:shadow-2xl hover:shadow-cyan-400/40 transition-all duration-300 flex items-center gap-3 border border-cyan-400/40 group"
                >
                  <span>Start Your AI Transformation</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/projects"
                  className="border-2 border-cyan-400/60 text-cyan-400 px-10 py-5 rounded-2xl font-light tracking-wide hover:bg-cyan-400/10 hover:border-cyan-400 transition-all duration-300 flex items-center gap-3"
                >
                  <span>View Case Studies</span>
                  <ArrowRight className="w-5 h-5 hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;